import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf, Users, ShieldCheck } from "lucide-react";
import { RevealText, Reveal } from "@/components/site/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1593679916468-851527f3ee27?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const SUSTAIN_IMG = "https://images.pexels.com/photos/17789406/pexels-photo-17789406.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

const SUSTAINABILITY = [
  "At Freight IQ, we recognize that global trade must go hand in hand with global responsibility. As a Canadian freight forwarding company with international reach, we are committed to building a more sustainable and resilient supply chain that supports the planet, our partners, and future generations.",
  "Sustainability is embedded in our operations through continuous innovation, smarter logistics strategies, and responsible partnerships. We actively work to reduce our environmental footprint by optimizing transportation routes, leveraging low-emission carriers, adopting digital documentation, and promoting intermodal solutions that balance efficiency with carbon reduction.",
  "We support our clients in meeting their sustainability goals by offering greener logistics options, carbon offsetting programs, and full visibility into environmental impact metrics. Across our operations, we champion waste reduction, energy efficiency, and ethical sourcing practices, while aligning with evolving global standards, including Canada's Net-Zero 2050 goals and the UN Sustainable Development Goals (SDGs).",
  "As we expand our global presence, our sustainability promise remains clear: to move goods responsibly, reduce our impact, and contribute to a cleaner, smarter supply chain—every step of the way.",
];

const PILLARS = [
  {
    icon: Users, title: "People & Communities",
    img: "https://images.unsplash.com/photo-1542323228-002ac256e7b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    body: "We value our employees, partners, and the communities we serve. We foster a safe, inclusive, and respectful work environment where diversity is celebrated and professional growth is supported. We give back through community engagement, charitable partnerships, and initiatives that promote education, equity, justice and economic development—locally and globally.",
  },
  {
    icon: Leaf, title: "Environmental Stewardship",
    img: "https://images.unsplash.com/photo-1476231682828-37e571bc172f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    body: "We are committed to reducing the environmental impact of our operations. Through route optimization, digital transformation, sustainable packaging, and collaboration with eco-conscious carriers, we strive to lower emissions and minimize waste. We support global efforts toward a low-carbon economy and align with Canada's environmental goals, including Net-Zero 2050.",
  },
  {
    icon: ShieldCheck, title: "Ethical Growth & Governance",
    img: "https://images.unsplash.com/photo-1672380135241-c024f7fbfa13?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    body: "Integrity guides every decision we make. We uphold the highest standards in ethical business practices, regulatory compliance, data security, and supply chain transparency. As we expand globally, we remain accountable to the communities we operate in and the values we represent.",
  },
];

export default function Sustainability() {
  return (
    <div data-testid="sustainability-page">
      <section className="relative pt-40 pb-20 mx-auto max-w-[1600px] px-5 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-6">/ Sustainability & CSR</p>
        <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-[12vw] sm:text-[9vw] lg:text-[7vw]">
          <RevealText lines={["Global trade,", "global", "responsibility."]} testid="page-title" />
        </h1>
      </section>

      <section className="relative h-[60vh] overflow-hidden">
        <img src={HERO_IMG} alt="Aerial view of green forest" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
      </section>

      {/* Sustainability Statement */}
      <section data-testid="sustainability-statement" className="mx-auto max-w-[1600px] px-5 sm:px-8 py-28 grid lg:grid-cols-12 gap-16">
        <Reveal className="lg:col-span-4">
          <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">/ Statement</p>
          <h2 className="font-display text-3xl sm:text-5xl uppercase tracking-tighter leading-[0.9] sticky top-28">Sustainability Statement</h2>
        </Reveal>
        <div className="lg:col-span-8 space-y-6 text-white/60 text-lg leading-relaxed">
          {SUSTAINABILITY.map((p, i) => <Reveal key={i} delay={i * 0.04}><p>{p}</p></Reveal>)}
        </div>
      </section>

      <section className="relative h-[55vh] overflow-hidden">
        <img src={SUSTAIN_IMG} alt="Row of wind turbines" className="absolute inset-0 h-full w-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/20 to-transparent" />
        <div className="relative z-10 h-full mx-auto max-w-[1600px] px-5 sm:px-8 flex items-end pb-12">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-6xl uppercase tracking-tighter max-w-3xl drop-shadow-[0_2px_20px_rgba(0,0,0,0.8)]">Moving goods responsibly—every step of the way.</h2>
          </Reveal>
        </div>
      </section>

      {/* CSR */}
      <section data-testid="csr-statement" className="mx-auto max-w-[1600px] px-5 sm:px-8 py-28">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">/ Corporate Social Responsibility</p>
          <h2 className="font-display text-4xl sm:text-6xl uppercase tracking-tighter leading-[0.9] max-w-4xl">Responsible business is the foundation of long-term success.</h2>
          <p className="text-white/60 text-lg mt-6 max-w-3xl">
            As a Canadian freight forwarding company with a global vision, we are committed to creating positive, lasting impact for our people, our communities, and the planet. Our CSR approach is rooted in three core pillars.
          </p>
        </Reveal>

        <div className="mt-16 space-y-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={0.05}>
              <div className="grid lg:grid-cols-12 gap-0 border border-white/10 group hover:border-primary/50 transition-colors duration-500 overflow-hidden">
                <div className={`lg:col-span-4 relative h-64 lg:h-auto overflow-hidden ${i % 2 ? "lg:order-2" : ""}`}>
                  <img src={p.img} alt={p.title} className="absolute inset-0 h-full w-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700 ease-out" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <div className={`lg:col-span-8 p-8 sm:p-12 flex flex-col justify-center ${i % 2 ? "lg:order-1" : ""}`}>
                  <div className="flex items-center gap-4 mb-5">
                    <span className="h-11 w-11 grid place-items-center border border-primary/40 text-primary">
                      <p.icon size={20} />
                    </span>
                    <span className="font-mono text-xs text-primary">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl uppercase tracking-tight">{p.title}</h3>
                  <p className="text-white/60 text-lg mt-4 leading-relaxed">{p.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-16 text-white/80 text-xl sm:text-2xl leading-relaxed max-w-4xl">
            Freight IQ is driven by purpose, not just profit. Our commitment to corporate social responsibility ensures that our growth is not only sustainable, but also meaningful—for our stakeholders and for the world.
          </p>
        </Reveal>
      </section>

      <section className="border-t border-white/10 bg-[#080808]">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-24 flex flex-col sm:flex-row sm:items-center justify-between gap-8">
          <h2 className="font-display text-3xl sm:text-5xl uppercase tracking-tighter max-w-2xl">Partner with a forwarder that moves with purpose.</h2>
          <a href="mailto:sales@freight-iq.com?subject=New%20Enquiry%20-%20Freight%20IQ" data-testid="sustainability-cta" className="inline-flex items-center gap-3 hover-fill px-8 py-4 font-mono text-xs uppercase tracking-widest w-fit shrink-0">
            Connect with us <ArrowUpRight size={16} />
          </a>
        </div>
      </section>
    </div>
  );
}
